import { IGame } from "../interfaces";

export const compressIds = (ids: number[]): string => {
    if (!ids.length) return "";

    // Убираем дубли и сортируем
    const sorted = Array.from(new Set(ids)).sort((a, b) => a - b);
    const ranges: string[] = [];
    let start = sorted[0];
    let end = sorted[0];

    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === end + 1) {
            end = sorted[i]; // Продолжаем диапазон
        } else {
            // Диапазон прервался, записываем прошлый
            ranges.push(start === end ? `${start}` : `${start}-${end}`);
            start = sorted[i];
            end = sorted[i];
        }
    }
    // Записываем последний хвост
    ranges.push(start === end ? `${start}` : `${start}-${end}`);

    return ranges.join(",");
};

type ResolveCallback = (value: IGame) => void;
type RejectCallback = (reason?: any) => void;

class GameBatchLoader {
    private queue: Set<number> = new Set();
    private callbacks: Map<number, { resolve: ResolveCallback, reject: RejectCallback }[]> = new Map();
    private timeout: ReturnType<typeof setTimeout> | null = null;

    // Храним ссылку на функцию baseQuery от RTKQ
    private lastBaseQuery: any = null;

    private readonly BATCH_LIMIT = 100;

    public load(id: number, baseQuery: any): Promise<IGame> {
        return new Promise((resolve, reject) => {
            this.queue.add(id);
            this.lastBaseQuery = baseQuery; // Сохраняем/обновляем контекст запроса

            const existingCallbacks = this.callbacks.get(id) || [];
            this.callbacks.set(id, [...existingCallbacks, { resolve, reject }]);

            if (!this.timeout) {
                // Ждем 50мс перед отправкой пачки
                this.timeout = setTimeout(() => this.flush(), 50);
            }
        });
    }

    private async flush() {
        const idsToFetch = Array.from(this.queue);
        this.queue.clear();
        this.timeout = null;

        if (!this.lastBaseQuery || idsToFetch.length === 0) return;

        const chunks: number[][] = [];
        for (let i = 0; i < idsToFetch.length; i += this.BATCH_LIMIT) {
            chunks.push(idsToFetch.slice(i, i + this.BATCH_LIMIT));
        }

        await Promise.all(chunks.map(chunk => this.fetchChunk(chunk, this.lastBaseQuery)));
    }

    /**
     * Обрабатывает конкретную партию ID
     */
    private async fetchChunk(chunk: number[], baseQuery: any) {
        const compressedIdsStr = compressIds(chunk);

        try {
            const result = await baseQuery({
                url: "game",
                method: "GET",
                params: { "f[ids_list]": compressedIdsStr }
            });

            if (result.error) {
                throw result.error;
            }

            // Предполагаем, что бэкенд возвращает массив игр
            const games: IGame[] = (result.data as DefaultResponse<IGame[]>).data;

            // Раздаем успешные ответы
            games.forEach((game) => {
                const cbList = this.callbacks.get(game.id);
                if (cbList) {
                    cbList.forEach(cb => cb.resolve(game));
                    this.callbacks.delete(game.id);
                }
            });

            // Очищаем хвосты ТОЛЬКО для текущего чанка.
            // Если какие-то ID из этой партии не вернулись с бэкенда (удалили/скрыли),
            // реджектим их промисы.
            chunk.forEach(id => {
                const cbList = this.callbacks.get(id);
                if (cbList) {
                    cbList.forEach(cb => cb.reject(new Error(`Game ${id} not found`)));
                    this.callbacks.delete(id);
                }
            });

        } catch (e) {
            // Если упал запрос ИМЕННО ЭТОЙ партии (например, 500 ошибка),
            // реджектим промисы только для ID из этого чанка. Остальные чанки могут отработать успешно.
            chunk.forEach(id => {
                const cbList = this.callbacks.get(id);
                if (cbList) {
                    cbList.forEach(cb => cb.reject(e));
                    this.callbacks.delete(id);
                }
            });
        }
    }
}

const gameBatchLoader = new GameBatchLoader();
export default gameBatchLoader